import { useState, useCallback, useEffect } from 'react'
import {
  styled,
  Popover,
  Button as MuiButton,
  SelectChangeEvent,
} from '@mui/material'
import { intl } from '../../../../i18n'
import { colorPalette } from '../../../style/colorPalette'
import DateVO from '../../../../vo/DateVO'
import Select from '../../../components/editors/select/Select'
import { DateSelect } from '../../../components/toolbars/common'
import { DateSelectType } from '../../../components/toolbars/common/DateSelect'
import { ArrowDropDown } from '@mui/icons-material'

export enum EndDatePreset {
  YESTERDAY = 'YESTERDAY',
  TODAY = 'TODAY',
  END_OF_MONTH = 'END_OF_MONTH',
  DATE_SPECIFIED = 'DATE_SPECIFIED',
}

type Props = {
  value?: DateVO
  onChange: (date: DateVO) => void
  disabled?: boolean
  preset?: EndDatePreset
  onPresetChange?: (preset: EndDatePreset) => void
  targetMonth?: DateVO
}

const EndDateSelector = ({
  value,
  onChange,
  disabled = false,
  preset: externalPreset,
  onPresetChange,
  targetMonth,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [preset, setPreset] = useState<EndDatePreset>(
    EndDatePreset.END_OF_MONTH
  )
  const [selectedDate, setSelectedDate] = useState<DateVO>(
    value || DateVO.now()
  )

  useEffect(() => {
    if (value) {
      setSelectedDate(value)
    }
  }, [value])

  useEffect(() => {
    if (externalPreset) {
      setPreset(externalPreset)
    }
  }, [externalPreset])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const getDateFromPreset = useCallback(
    (presetType: EndDatePreset): DateVO => {
      const now = DateVO.now()
      switch (presetType) {
        case EndDatePreset.YESTERDAY:
          return now.addDays(-1)
        case EndDatePreset.TODAY:
          return now
        case EndDatePreset.END_OF_MONTH:
          return now.getLastDayOfMonth()
        case EndDatePreset.DATE_SPECIFIED:
          return selectedDate
        default:
          return now
      }
    },
    [selectedDate]
  )

  const handlePresetChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      const newPreset = e.target.value as EndDatePreset
      setPreset(newPreset)
      const newDate = getDateFromPreset(newPreset)
      setSelectedDate(newDate)
      onPresetChange?.(newPreset)
    },
    [getDateFromPreset, onPresetChange]
  )

  const handleDateChange = useCallback(
    (dateValue: string | undefined) => {
      if (dateValue) {
        const newDate = new DateVO(dateValue)
        setSelectedDate(newDate)
        setPreset(EndDatePreset.DATE_SPECIFIED)
        onPresetChange?.(EndDatePreset.DATE_SPECIFIED)
      }
    },
    [onPresetChange]
  )

  const handleApply = useCallback(() => {
    onChange(selectedDate)
    handleClose()
  }, [selectedDate, onChange])

  const getPresetLabel = (presetType: EndDatePreset): string => {
    switch (presetType) {
      case EndDatePreset.YESTERDAY:
        return intl.formatMessage({ id: 'yesterday' })
      case EndDatePreset.TODAY:
        return intl.formatMessage({ id: 'today' })
      case EndDatePreset.END_OF_MONTH:
        return intl.formatMessage({ id: 'endOfMonth' })
      case EndDatePreset.DATE_SPECIFIED:
        return intl.formatMessage({ id: 'dateSpecified' })
      default:
        return ''
    }
  }

  const displayDate = value || DateVO.now()

  return (
    <>
      <SelectorButton onClick={handleClick} disabled={disabled}>
        <DateLabel>{displayDate.format()}</DateLabel>
        <ArrowDropDown style={{ fontSize: '18px' }} />
      </SelectorButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
            },
          },
        }}
      >
        <PopoverContent>
          <SelectorsRow>
            <PresetSelectContainer>
              <Select
                fullWidth={true}
                value={preset}
                onChange={handlePresetChange}
                disableUnderline={true}
                options={[
                  {
                    value: EndDatePreset.YESTERDAY,
                    label: getPresetLabel(EndDatePreset.YESTERDAY),
                  },
                  {
                    value: EndDatePreset.TODAY,
                    label: getPresetLabel(EndDatePreset.TODAY),
                  },
                  {
                    value: EndDatePreset.END_OF_MONTH,
                    label: getPresetLabel(EndDatePreset.END_OF_MONTH),
                  },
                  {
                    value: EndDatePreset.DATE_SPECIFIED,
                    label: getPresetLabel(EndDatePreset.DATE_SPECIFIED),
                  },
                ]}
                sx={{
                  height: '32px',
                  fontSize: '12px',
                }}
              />
            </PresetSelectContainer>
            <DateInputContainer>
              <DateSelect
                type={DateSelectType.DATE}
                value={selectedDate}
                onChange={handleDateChange}
                placeholder="YYYY/MM/DD"
                disabled={preset !== EndDatePreset.DATE_SPECIFIED}
                targetMonth={targetMonth}
              />
            </DateInputContainer>
          </SelectorsRow>
          <ApplyButton onClick={handleApply}>
            {intl.formatMessage({ id: 'dialog.apply' })}
          </ApplyButton>
        </PopoverContent>
      </Popover>
    </>
  )
}

const SelectorButton = styled(MuiButton)({
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  height: '28px',
  padding: '4px 8px',
  fontSize: '12px',
  color: colorPalette.monotone[5],
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const DateLabel = styled('span')({
  fontSize: '12px',
  fontWeight: 'normal',
})

const PopoverContent = styled('div')({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  minWidth: '300px',
  overflow: 'visible',
})

const SelectorsRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
})

const PresetSelectContainer = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '0 8px',
  minWidth: '120px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
})

const DateInputContainer = styled('div')({
  minWidth: '140px',
  flex: 1,
  zIndex: 1500,
  position: 'relative',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
})

const ApplyButton = styled(MuiButton)({
  backgroundColor: colorPalette.skyBlue[7],
  color: colorPalette.monotone[0],
  borderRadius: '4px',
  height: '32px',
  fontSize: '12px',
  fontWeight: 'bold',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[5],
  },
})

export default EndDateSelector
